import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Toaster, toast } from "sonner";
import { Check, Loader2, PenLine, Eraser, Upload, X, FileImage } from "lucide-react";

const BACKEND_URL = (() => {
  const env = process.env.REACT_APP_BACKEND_URL;
  if (env && typeof env === "string" && env.startsWith("http")) return env.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location && window.location.origin) return window.location.origin;
  return "";
})();
const API = `${BACKEND_URL}/api`;
const BRAND_GREEN = "#2e3104";
const LOGO_URL = "/images/logo_ar.webp";
const CHUNK_SIZE = 256 * 1024; // 256 KB per chunk

const RENTAL_TERMS_SV = [
  {
    title: "1. Övertagande av risk",
    body: "Jag är medveten om och accepterar att körning med motorcykel innebär betydande risker, inklusive men inte begränsat till allvarlig personskada, permanent invaliditet eller dödsfall. Jag bekräftar att all körning sker helt på egen risk, oavsett vägförhållanden, väder, terräng, trafik, andra trafikanters agerande eller andra omständigheter. Jag ansvarar själv för min körning, mina beslut och min förmåga att framföra motorcykeln på ett säkert och lagligt sätt.",
  },
  {
    title: "2. Ansvarsfriskrivning",
    body: "Jag avstår härmed, i den utsträckning som är tillåten enligt svensk lag, från att rikta krav mot Arctic Riders, dess ägare, anställda, guider, samarbetspartners och entreprenörer för personskador, dödsfall, egendomsskador eller andra krav som kan uppstå i samband med hyra av motorcykel eller deltagande i guidad tur. Detta gäller oavsett om skadan uppstår genom olycka, trafikförhållanden, väder, vägunderlag, andra trafikanters agerande, felbedömningar från min sida eller annan omständighet som ligger utanför Arctic Riders direkta kontroll.",
  },
  {
    title: "3. Ansvar för skador på motorcykeln",
    body: "Jag accepterar att jag är fullt ekonomiskt ansvarig för alla skador som uppstår på den hyrda motorcykeln under hyres- eller turperioden. Detta inkluderar bland annat: repor och kosmetiska skador, skador efter omkullkörning eller olycka, mekaniska skador, skador på plast, kåpor, styre, hjul, däck, fjädring, motor, elektronik och övriga komponenter, kostnader för reservdelar, verkstadskostnader och arbetskostnad för reparation, transport- och bärgningskostnader, kostnader för felsökning och återställning. Normalt slitage vid korrekt användning omfattas inte. Jag accepterar att ersätta Arctic Riders för samtliga reparations-, återställnings- eller ersättningskostnader som uppstår till följd av skada på motorcykeln under min hyres-/turperiod.",
  },
  {
    title: "4. Ansvar vid stöld, förlust eller utebliven återlämning",
    body: "Jag ansvarar fullt ut för motorcykeln under hela den tid den är i min besittning. Om motorcykeln blir stulen, förlorad, beslagtas på grund av mitt agerande eller av annan anledning inte återlämnas till Arctic Riders, förbinder jag mig att ersätta Arctic Riders med motorcykelns fulla marknadsvärde eller återanskaffningsvärde. Jag ansvarar även för kostnader kopplade till nycklar, utrustning, transport, administration och eventuella följdkostnader som uppstår till följd av stöld, förlust eller utebliven återlämning.",
  },
  {
    title: "5. Körning och efterlevnad av lagar",
    body: "Jag förbinder mig att: endast framföra motorcykeln på vägar, leder och områden där motorcykelkörning är tillåten, följa samtliga svenska lagar, trafikregler och myndighetsföreskrifter, följa Terrängkörningslagen (1975:1313) och annan tillämplig lagstiftning, inte köra i terräng eller på annan mark där motorfordon inte är tillåtna, visa hänsyn till natur, markägare, boende och andra trafikanter. Jag ansvarar själv för samtliga böter, sanktionsavgifter, bärgningskostnader, beslag, skadestånd eller andra kostnader som uppstår till följd av att jag bryter mot gällande lagstiftning, trafikregler, myndighetsföreskrifter eller detta avtal.",
  },
  {
    title: "6. Förarens ansvar och behörighet",
    body: "Jag intygar att: jag innehar giltigt körkort för den aktuella motorcykelklassen, jag har tillräcklig erfarenhet för att framföra motorcykeln på ett säkert sätt, jag inte är påverkad av alkohol, droger, läkemedel eller andra ämnen som kan påverka min körförmåga, jag kommer att använda hjälm under all körning, jag kommer att använda lämplig skyddsutrustning, jag själv ansvarar för att framföra motorcykeln på ett säkert, ansvarsfullt och lagligt sätt. Arctic Riders har rätt att neka utlämning av motorcykel eller avbryta deltagande i tur om Företaget bedömer att jag saknar tillräcklig körförmåga, är påverkad, agerar vårdslöst eller på annat sätt utgör en risk för mig själv, andra deltagare, tredje man eller egendom.",
  },
  {
    title: "7. Olyckor, skador och bärgning",
    body: "Vid olycka, avkörning, tekniskt fel orsakat av felaktig användning eller annan händelse som gör att motorcykeln inte kan framföras, ansvarar jag för samtliga kostnader som uppstår, inklusive men inte begränsat till: bärgning, transport av motorcykeln, verkstadskostnader, reservdelar, hämtning eller återförande av motorcykeln, administrativa kostnader, eventuella kostnader till följd av att motorcykeln inte kan hyras ut under reparationstiden, i den utsträckning detta är tillåtet enligt svensk lag. Jag förbinder mig att omedelbart meddela Arctic Riders vid olycka, skada, tekniskt problem, stöld eller annan incident.",
  },
  {
    title: "8. Utrustning",
    body: "Jag ansvarar för all utrustning som tillhandahålls av Arctic Riders under hyres- eller turperioden, inklusive men inte begränsat till hjälm, skyddsutrustning, nycklar, väskor, GPS, kommunikationsutrustning och övriga tillbehör. Jag accepterar att ersätta Arctic Riders för skadad, förlorad eller ej återlämnad utrustning.",
  },
  {
    title: "9. Deposition och debitering",
    body: "Jag accepterar att Arctic Riders har rätt att debitera mig för kostnader som uppstår enligt detta avtal, inklusive men inte begränsat till: skador på motorcykeln, skador på eller förlust av utrustning, självrisker, stöld eller förlust, böter och avgifter, bärgning och transport, försenad återlämning, utebliven återlämning, andra kostnader som uppkommer under eller till följd av hyres-/turperioden.",
  },
  {
    title: "10. Försenad återlämning",
    body: "Motorcykeln ska återlämnas på överenskommen tid och plats. Om motorcykeln återlämnas för sent har Arctic Riders rätt att debitera ytterligare hyra samt eventuella kostnader som uppstår till följd av den försenade återlämningen.",
  },
  {
    title: "11. Tredjepartsansvar",
    body: "Jag förbinder mig att hålla Arctic Riders skadeslöst från krav, skadestånd, ersättningsanspråk eller rättsliga krav från tredje man som uppstår till följd av mitt användande av motorcykeln eller mitt agerande under hyres-/turperioden. Detta inkluderar krav från andra trafikanter, passagerare, markägare, myndigheter eller andra personer.",
  },
  {
    title: "12. Tillämplig lag och tvist",
    body: "Detta avtal regleras av svensk lag. Eventuella tvister som uppstår med anledning av detta avtal ska i första hand lösas genom dialog mellan parterna. Om tvisten inte kan lösas på annat sätt ska den avgöras av svensk allmän domstol.",
  },
  {
    title: "13. Bekräftelse",
    body: "Jag bekräftar att jag har läst, förstått och accepterat detta avtal i sin helhet. Jag förstår att detta avtal innebär att jag tar på mig betydande ansvar och ekonomiska skyldigheter vid skador, stöld, förlust, lagbrott eller annan händelse kopplad till mitt användande av motorcykeln.",
  },
];

const T = {
  sv: {
    title: "Digitalt hyresavtal",
    subtitle: "Arctic Riders — signera avtalet nedan",
    loading: "Hämtar avtal...",
    notFound: "Avtalet hittades inte eller har utgått.",
    alreadySigned: "Detta avtal har redan signerats. Tack!",
    rentalDetails: "Hyresdetaljer",
    rentalItem: "Hyresobjekt",
    registration: "Registreringsnummer",
    date: "Datum",
    endDate: "Slutdatum",
    riders: "Antal förare",
    amount: "Belopp",
    driver: "Föraruppgifter",
    driverName: "Fullständigt namn",
    driverEmail: "E-post",
    driverPhone: "Telefon",
    driverPid: "Personnummer",
    driverAddr: "Adress",
    driverLicense: "Körkortsnummer",
    licenseUpload: "Ladda upp körkort (bild)",
    licenseUploadHint: "Ladda upp en tydlig bild av ditt körkort (JPG, PNG, WEBP eller HEIC — max 12 MB).",
    licenseSelectBtn: "Välj fil",
    licenseUploading: "Laddar upp...",
    licenseUploaded: "Körkort uppladdat",
    licenseRemove: "Ta bort",
    licenseRequired: "Vänligen ladda upp en bild på ditt körkort.",
    licenseTooLarge: "Filen är för stor (max 12 MB).",
    licenseInvalid: "Endast bildfiler är tillåtna (JPG, PNG, WEBP, HEIC).",
    licenseUploadFailed: "Uppladdning misslyckades — försök igen.",
    company: "Företagsfaktura (frivilligt)",
    companyToggle: "Fakturera till företag",
    companyName: "Företagsnamn",
    companyOrg: "Organisationsnummer",
    companyAddr: "Faktureringsadress",
    companyEmail: "E-post för faktura",
    signature: "Din underskrift",
    signatureHint: "Rita din signatur i rutan nedan med muspekare eller finger.",
    clear: "Rensa",
    terms:
      "Jag intygar att uppgifterna ovan är korrekta, att jag innehar giltigt motorcykelkörkort och accepterar Arctic Riders hyresvillkor.",
    termsLinkLabel: "Läs villkoren",
    submit: "Signera avtalet",
    submitting: "Signerar...",
    success: "Tack! Avtalet är signerat. En kopia har skickats till din e-post.",
    required: "Vänligen fyll i alla obligatoriska fält.",
    signRequired: "Vänligen rita din signatur.",
    termsRequired: "Du måste acceptera villkoren.",
    time_morning: "09:00 - 14:00",
    time_afternoon: "14:00 - 19:00",
    timeSlot: "Tidsslot",
    autoSaving: "Sparar...",
    autoSaved: "Automatiskt sparat",
    autoSaveHint: "Dina uppgifter sparas automatiskt. Du kan stänga fliken och återkomma senare.",
    footer: "Arctic Riders · Org.nr 559205-3432",
  },
  en: {
    title: "Digital rental agreement",
    subtitle: "Arctic Riders — sign the agreement below",
    loading: "Loading agreement...",
    notFound: "Agreement not found or expired.",
    alreadySigned: "This agreement has already been signed. Thank you!",
    rentalDetails: "Rental details",
    rentalItem: "Rental item",
    registration: "Registration number",
    date: "Date",
    endDate: "End date",
    riders: "Number of riders",
    amount: "Amount",
    driver: "Driver details",
    driverName: "Full name",
    driverEmail: "Email",
    driverPhone: "Phone",
    driverPid: "Personal ID",
    driverAddr: "Address",
    driverLicense: "Driver's license number",
    licenseUpload: "Upload driver's licence (photo)",
    licenseUploadHint: "Upload a clear photo of your driver's licence (JPG, PNG, WEBP or HEIC — max 12 MB).",
    licenseSelectBtn: "Choose file",
    licenseUploading: "Uploading...",
    licenseUploaded: "Licence uploaded",
    licenseRemove: "Remove",
    licenseRequired: "Please upload a photo of your driver's licence.",
    licenseTooLarge: "File is too large (max 12 MB).",
    licenseInvalid: "Only image files are allowed (JPG, PNG, WEBP, HEIC).",
    licenseUploadFailed: "Upload failed — please try again.",
    company: "Company invoice (optional)",
    companyToggle: "Bill company instead",
    companyName: "Company name",
    companyOrg: "Organization number",
    companyAddr: "Billing address",
    companyEmail: "Invoice email",
    signature: "Your signature",
    signatureHint: "Draw your signature in the box below.",
    clear: "Clear",
    terms:
      "I confirm the information above is correct, that I hold a valid motorcycle licence and accept Arctic Riders' rental terms.",
    termsLinkLabel: "Read the terms",
    submit: "Sign agreement",
    submitting: "Signing...",
    success: "Thank you! Agreement signed. A copy has been sent to your email.",
    required: "Please fill in all required fields.",
    signRequired: "Please draw your signature.",
    termsRequired: "You must accept the terms.",
    time_morning: "09:00 - 14:00",
    time_afternoon: "14:00 - 19:00",
    timeSlot: "Time slot",
    autoSaving: "Saving...",
    autoSaved: "Auto-saved",
    autoSaveHint: "Your details are saved automatically. You can close the tab and return later.",
    footer: "Arctic Riders · Org.nr 559205-3432",
  },
};

const formatAmount = (n) =>
  `${new Intl.NumberFormat("sv-SE").format(Math.round(n || 0))} kr`;

export default function SignAgreement() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [agreement, setAgreement] = useState(null);
  const [error, setError] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    driver_name: "",
    driver_email: "",
    driver_phone: "",
    driver_personal_id: "",
    driver_address: "",
    driver_license_number: "",
    company_billing: false,
    company_name: "",
    company_org_number: "",
    company_address: "",
    company_invoice_email: "",
    accepted_terms: false,
  });

  const sigRef = useRef(null);
  const [sigEmpty, setSigEmpty] = useState(true);

  // Auto-save state
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved
  const saveTimerRef = useRef(null);
  const hasRestoredRef = useRef(false);

  // License upload state
  const [licenseFile, setLicenseFile] = useState(null);
  const [licensePreview, setLicensePreview] = useState(null);
  const [licenseUploadId, setLicenseUploadId] = useState(null);
  const [licenseUploadProgress, setLicenseUploadProgress] = useState(0);
  const [licenseUploadStatus, setLicenseUploadStatus] = useState("idle"); // idle|uploading|complete|error
  const [licenseUploadError, setLicenseUploadError] = useState("");
  const licenseInputRef = useRef(null);

  const lang = (agreement?.language || "sv").toLowerCase() === "en" ? "en" : "sv";
  const t = T[lang];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/agreements/${id}`);
        if (cancelled) return;
        setAgreement(data);
        if (data.status === "signed") setAlreadySigned(true);

        // Restore draft (persists across reloads) or fall back to prefill_email
        const draft = data.draft || {};
        setForm((f) => ({
          ...f,
          driver_name: draft.driver_name ?? f.driver_name,
          driver_email: draft.driver_email ?? data.prefill_email ?? f.driver_email,
          driver_phone: draft.driver_phone ?? f.driver_phone,
          driver_personal_id: draft.driver_personal_id ?? f.driver_personal_id,
          driver_address: draft.driver_address ?? f.driver_address,
          driver_license_number: draft.driver_license_number ?? f.driver_license_number,
          company_billing: draft.company_billing ?? f.company_billing,
          company_name: draft.company_name ?? f.company_name,
          company_org_number: draft.company_org_number ?? f.company_org_number,
          company_address: draft.company_address ?? f.company_address,
          company_invoice_email: draft.company_invoice_email ?? f.company_invoice_email,
          accepted_terms: draft.accepted_terms ?? f.accepted_terms,
        }));
        // Restore license reference
        if (draft.license_file_id) {
          setLicenseUploadId(draft.license_file_id);
          setLicenseUploadStatus("complete");
          setLicenseUploadProgress(100);
        }
        // Prevent auto-save from firing while restoring initial values
        hasRestoredRef.current = true;
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  // Debounced auto-save to the server (survives page reloads)
  const draftPayload = useMemo(() => ({
    driver_name: form.driver_name,
    driver_email: form.driver_email,
    driver_phone: form.driver_phone,
    driver_personal_id: form.driver_personal_id,
    driver_address: form.driver_address,
    driver_license_number: form.driver_license_number,
    company_billing: form.company_billing,
    company_name: form.company_name,
    company_org_number: form.company_org_number,
    company_address: form.company_address,
    company_invoice_email: form.company_invoice_email,
    accepted_terms: form.accepted_terms,
    license_file_id: licenseUploadId || null,
  }), [form, licenseUploadId]);

  useEffect(() => {
    if (!hasRestoredRef.current) return;      // don't save until restore is done
    if (alreadySigned || success) return;     // don't save after signing
    if (!agreement || agreement.status !== "pending") return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus("saving");
    saveTimerRef.current = setTimeout(async () => {
      try {
        await axios.post(`${API}/agreements/${id}/draft`, draftPayload);
        setSaveStatus("saved");
      } catch {
        setSaveStatus("idle");
      }
    }, 800);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [draftPayload, id, agreement, alreadySigned, success]);

  const clearSignature = () => {
    if (sigRef.current) {
      sigRef.current.clear();
      setSigEmpty(true);
    }
  };

  const uploadLicenseFile = async (file) => {
    setLicenseUploadError("");
    setLicenseUploadProgress(0);
    setLicenseUploadStatus("uploading");
    const isImage =
      file.type.startsWith("image/") ||
      /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "");
    if (!isImage) {
      setLicenseUploadStatus("error");
      setLicenseUploadError(t.licenseInvalid);
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setLicenseUploadStatus("error");
      setLicenseUploadError(t.licenseTooLarge);
      return;
    }
    const uploadId =
      (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      `up-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result || "";
            const idx = result.indexOf(",");
            resolve(idx >= 0 ? result.slice(idx + 1) : result);
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        await axios.post(`${API}/upload/license/chunk`, {
          upload_id: uploadId,
          chunk_index: i,
          total_chunks: totalChunks,
          file_name: file.name,
          mime_type: file.type || "image/jpeg",
          data: base64,
        });
        setLicenseUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      setLicenseUploadId(uploadId);
      setLicenseUploadStatus("complete");
    } catch (err) {
      setLicenseUploadStatus("error");
      setLicenseUploadError(err?.response?.data?.detail || t.licenseUploadFailed);
    }
  };

  const handleLicenseSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLicenseFile(file);
    if (licensePreview) URL.revokeObjectURL(licensePreview);
    setLicensePreview(URL.createObjectURL(file));
    uploadLicenseFile(file);
  };

  const removeLicense = () => {
    if (licensePreview) URL.revokeObjectURL(licensePreview);
    setLicenseFile(null);
    setLicensePreview(null);
    setLicenseUploadId(null);
    setLicenseUploadProgress(0);
    setLicenseUploadStatus("idle");
    setLicenseUploadError("");
    if (licenseInputRef.current) licenseInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Basic validation
    if (
      !form.driver_name.trim() ||
      !form.driver_email.trim() ||
      !form.driver_phone.trim()
    ) {
      toast.error(t.required);
      return;
    }
    if (!form.accepted_terms) {
      toast.error(t.termsRequired);
      return;
    }
    if (!licenseUploadId || licenseUploadStatus !== "complete") {
      toast.error(t.licenseRequired);
      return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error(t.signRequired);
      return;
    }

    const signature_data = sigRef.current.toDataURL("image/png");

    // Build clean payload — omit empty optional fields (backend Optional[EmailStr] rejects "")
    const payload = {
      driver_name: form.driver_name.trim(),
      driver_email: form.driver_email.trim(),
      driver_phone: form.driver_phone.trim(),
      driver_personal_id: form.driver_personal_id.trim() || null,
      driver_address: form.driver_address.trim() || null,
      driver_license_number: form.driver_license_number.trim() || null,
      company_billing: form.company_billing,
      accepted_terms: form.accepted_terms,
      signature_data,
      license_file_id: licenseUploadId,
    };
    if (form.company_billing) {
      payload.company_name = form.company_name.trim() || null;
      payload.company_org_number = form.company_org_number.trim() || null;
      payload.company_address = form.company_address.trim() || null;
      payload.company_invoice_email = form.company_invoice_email.trim() || null;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/agreements/${id}/sign`, payload);
      setSuccess(true);
      toast.success(t.success);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((x) => x?.msg || "Invalid value").join("; ")
        : typeof detail === "string"
        ? detail
        : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span data-testid="agreement-loading">{T.sv.loading}</span>
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="agreement-not-found">
          <h1 className="text-2xl font-bold mb-2">404</h1>
          <p className="text-neutral-400">{T.sv.notFound}</p>
          <Link to="/" className="text-[#a3a63c] mt-4 inline-block">
            arcticriders.com
          </Link>
        </div>
      </div>
    );
  }

  if (alreadySigned || success) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center px-4">
        <div className="text-center max-w-lg" data-testid="agreement-signed-success">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">
            {success ? t.success : t.alreadySigned}
          </h1>
          <p className="text-neutral-400 text-sm mt-2">
            Ref: {(agreement?.id || "").slice(0, 8).toUpperCase()}
          </p>
          <Link to="/" className="text-[#a3a63c] mt-6 inline-block">
            arcticriders.com
          </Link>
        </div>
      </div>
    );
  }

  const itemName = agreement?.package_name || agreement?.variant_name || "Motorcycle rental";
  const timeSlotDisplay =
    agreement?.time_slot === "morning"
      ? t.time_morning
      : agreement?.time_slot === "afternoon"
      ? t.time_afternoon
      : null;

  return (
    <HelmetProvider>
      <Helmet>
        <title>Digitalt hyresavtal — Arctic Riders</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Toaster position="top-center" richColors />
      <div className="min-h-screen bg-neutral-950 text-white py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8 border-b border-neutral-800 pb-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <img
                src={LOGO_URL}
                alt="Arctic Riders"
                className="h-14 sm:h-16 w-auto"
                data-testid="agreement-logo"
              />
              {/* Auto-save indicator */}
              {!alreadySigned && !success && (
                <div
                  className="flex items-center gap-1.5 text-xs text-neutral-500"
                  data-testid="autosave-status"
                >
                  {saveStatus === "saving" && (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>{t.autoSaving}</span>
                    </>
                  )}
                  {saveStatus === "saved" && (
                    <>
                      <Check className="w-3 h-3 text-green-500" />
                      <span className="text-green-500">{t.autoSaved}</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold" data-testid="agreement-title">
              {t.title}
            </h1>
            <p className="text-neutral-400 mt-1 text-sm">{t.subtitle}</p>
            {!alreadySigned && !success && (
              <p className="text-neutral-500 text-xs mt-2 italic">{t.autoSaveHint}</p>
            )}
          </div>

          {/* Rental details */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 mb-6">
            <h2 className="text-xs uppercase tracking-widest text-[#a3a63c] font-semibold mb-4">
              {t.rentalDetails}
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 text-sm">
              <div>
                <dt className="text-neutral-500">{t.rentalItem}</dt>
                <dd className="font-medium">{itemName}</dd>
              </div>
              {agreement?.motorcycle_registration && (
                <div>
                  <dt className="text-neutral-500">{t.registration}</dt>
                  <dd className="font-medium uppercase tracking-wider" data-testid="agreement-registration">
                    {agreement.motorcycle_registration}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-neutral-500">{t.date}</dt>
                <dd className="font-medium" data-testid="agreement-date">
                  {(agreement?.date || "").slice(0, 10)}
                </dd>
              </div>
              {agreement?.end_date && (
                <div>
                  <dt className="text-neutral-500">{t.endDate}</dt>
                  <dd className="font-medium">{agreement.end_date.slice(0, 10)}</dd>
                </div>
              )}
              {timeSlotDisplay && (
                <div>
                  <dt className="text-neutral-500">{t.timeSlot}</dt>
                  <dd className="font-medium">{timeSlotDisplay}</dd>
                </div>
              )}
              <div>
                <dt className="text-neutral-500">{t.riders}</dt>
                <dd className="font-medium">{agreement?.riders || 1}</dd>
              </div>
              <div>
                <dt className="text-neutral-500">{t.amount}</dt>
                <dd className="font-bold text-[#a3a63c]" data-testid="agreement-amount">
                  {formatAmount(agreement?.amount)}
                </dd>
              </div>
            </dl>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Driver */}
            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5">
              <h2 className="text-xs uppercase tracking-widest text-[#a3a63c] font-semibold mb-4">
                {t.driver}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>{t.driverName} *</Label>
                  <Input
                    name="driver_name"
                    value={form.driver_name}
                    onChange={handleChange}
                    required
                    data-testid="driver-name-input"
                    className="bg-neutral-950 border-neutral-700 mt-1"
                  />
                </div>
                <div>
                  <Label>{t.driverEmail} *</Label>
                  <Input
                    type="email"
                    name="driver_email"
                    value={form.driver_email}
                    onChange={handleChange}
                    required
                    data-testid="driver-email-input"
                    className="bg-neutral-950 border-neutral-700 mt-1"
                  />
                </div>
                <div>
                  <Label>{t.driverPhone} *</Label>
                  <Input
                    name="driver_phone"
                    value={form.driver_phone}
                    onChange={handleChange}
                    required
                    data-testid="driver-phone-input"
                    className="bg-neutral-950 border-neutral-700 mt-1"
                  />
                </div>
                <div>
                  <Label>{t.driverPid}</Label>
                  <Input
                    name="driver_personal_id"
                    value={form.driver_personal_id}
                    onChange={handleChange}
                    data-testid="driver-pid-input"
                    className="bg-neutral-950 border-neutral-700 mt-1"
                  />
                </div>
                <div>
                  <Label>{t.driverLicense}</Label>
                  <Input
                    name="driver_license_number"
                    value={form.driver_license_number}
                    onChange={handleChange}
                    data-testid="driver-license-input"
                    className="bg-neutral-950 border-neutral-700 mt-1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>{t.driverAddr}</Label>
                  <Textarea
                    name="driver_address"
                    value={form.driver_address}
                    onChange={handleChange}
                    rows={2}
                    data-testid="driver-address-input"
                    className="bg-neutral-950 border-neutral-700 mt-1"
                  />
                </div>
              </div>
            </div>

            {/* License upload */}
            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5" data-testid="license-upload-section">
              <h2 className="text-xs uppercase tracking-widest text-[#a3a63c] font-semibold flex items-center gap-2 mb-2">
                <FileImage className="w-4 h-4" />
                {t.licenseUpload} *
              </h2>
              <p className="text-neutral-500 text-xs mb-4">{t.licenseUploadHint}</p>

              <input
                ref={licenseInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                onChange={handleLicenseSelect}
                className="hidden"
                data-testid="license-file-input"
              />

              {licenseUploadStatus === "idle" && (
                <button
                  type="button"
                  onClick={() => licenseInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 bg-neutral-950 hover:bg-neutral-800 border border-dashed border-neutral-600 hover:border-[#a3a63c] text-neutral-300 py-4 rounded-md transition-colors"
                  data-testid="license-select-btn"
                >
                  <Upload className="w-4 h-4" />
                  <span className="text-sm font-medium">{t.licenseSelectBtn}</span>
                </button>
              )}

              {licenseUploadStatus === "uploading" && (
                <div className="bg-neutral-950 border border-neutral-700 rounded-md p-4" data-testid="license-uploading">
                  <div className="flex items-center gap-3 mb-2 text-sm text-neutral-300">
                    <Loader2 className="w-4 h-4 animate-spin text-[#a3a63c]" />
                    <span>
                      {t.licenseUploading} {licenseUploadProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-neutral-800 h-1.5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#a3a63c] transition-all"
                      style={{ width: `${licenseUploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {licenseUploadStatus === "complete" && (
                <div className="flex items-center gap-3 bg-neutral-950 border border-green-500/30 rounded-md p-3" data-testid="license-complete">
                  {licensePreview && (
                    <img
                      src={licensePreview}
                      alt="Körkort"
                      className="w-14 h-14 object-cover rounded border border-neutral-700"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                      <Check className="w-4 h-4" /> {t.licenseUploaded}
                    </div>
                    {licenseFile && (
                      <div className="text-neutral-500 text-xs mt-0.5 truncate">
                        {licenseFile.name} ({(licenseFile.size / 1024).toFixed(0)} KB)
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={removeLicense}
                    className="text-neutral-400 hover:text-red-400"
                    data-testid="license-remove-btn"
                    title={t.licenseRemove}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {licenseUploadStatus === "error" && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3" data-testid="license-error">
                  <p className="text-red-400 text-sm">{licenseUploadError}</p>
                  <button
                    type="button"
                    onClick={() => {
                      removeLicense();
                      licenseInputRef.current?.click();
                    }}
                    className="mt-2 text-xs text-neutral-300 underline hover:text-white"
                  >
                    {t.licenseSelectBtn}
                  </button>
                </div>
              )}
            </div>

            {/* Company invoice */}
            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5">
              <h2 className="text-xs uppercase tracking-widest text-[#a3a63c] font-semibold mb-4">
                {t.company}
              </h2>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="company_billing"
                  checked={form.company_billing}
                  onChange={handleChange}
                  data-testid="company-billing-toggle"
                  className="w-4 h-4 accent-[#2e3104]"
                />
                <span className="text-sm">{t.companyToggle}</span>
              </label>

              {form.company_billing && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label>{t.companyName} *</Label>
                    <Input
                      name="company_name"
                      value={form.company_name}
                      onChange={handleChange}
                      required={form.company_billing}
                      data-testid="company-name-input"
                      className="bg-neutral-950 border-neutral-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t.companyOrg} *</Label>
                    <Input
                      name="company_org_number"
                      value={form.company_org_number}
                      onChange={handleChange}
                      required={form.company_billing}
                      data-testid="company-org-input"
                      className="bg-neutral-950 border-neutral-700 mt-1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>{t.companyAddr} *</Label>
                    <Textarea
                      name="company_address"
                      value={form.company_address}
                      onChange={handleChange}
                      required={form.company_billing}
                      rows={2}
                      data-testid="company-address-input"
                      className="bg-neutral-950 border-neutral-700 mt-1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>{t.companyEmail} *</Label>
                    <Input
                      type="email"
                      name="company_invoice_email"
                      value={form.company_invoice_email}
                      onChange={handleChange}
                      required={form.company_billing}
                      data-testid="company-email-input"
                      className="bg-neutral-950 border-neutral-700 mt-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Rental terms */}
            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5" data-testid="rental-terms-section">
              <h2 className="text-xs uppercase tracking-widest text-[#a3a63c] font-semibold mb-4">
                {lang === "sv" ? "Hyresvillkor" : "Rental terms"}
              </h2>
              <div className="max-h-[320px] overflow-y-auto pr-3 space-y-4 text-sm text-neutral-300 border border-neutral-800 rounded-md p-4 bg-black/30">
                {RENTAL_TERMS_SV.map((term, i) => (
                  <div key={i}>
                    <h3 className="font-semibold text-white text-sm mb-1">{term.title}</h3>
                    <p className="text-neutral-400 text-xs leading-relaxed whitespace-pre-line">
                      {term.body}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral-500 mt-3">
                {lang === "sv"
                  ? "Scrolla för att läsa hela avtalet. Genom att signera nedan bekräftar du att du läst, förstått och accepterar samtliga villkor ovan."
                  : "Scroll to read the full agreement. By signing below you confirm you have read, understood and accepted all terms above."}
              </p>
            </div>

            {/* Signature */}
            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs uppercase tracking-widest text-[#a3a63c] font-semibold flex items-center gap-2">
                  <PenLine className="w-4 h-4" />
                  {t.signature}
                </h2>
                <button
                  type="button"
                  onClick={clearSignature}
                  data-testid="clear-signature-btn"
                  className="text-xs text-neutral-400 hover:text-white flex items-center gap-1"
                >
                  <Eraser className="w-3 h-3" /> {t.clear}
                </button>
              </div>
              <p className="text-neutral-500 text-xs mb-3">{t.signatureHint}</p>
              <div className="bg-white rounded-md overflow-hidden touch-none" data-testid="signature-canvas-wrapper">
                <SignatureCanvas
                  ref={sigRef}
                  penColor="#111827"
                  onEnd={() => setSigEmpty(sigRef.current?.isEmpty())}
                  canvasProps={{
                    width: 700,
                    height: 180,
                    className: "w-full h-[180px]",
                  }}
                />
              </div>
              {!sigEmpty && (
                <p className="text-xs text-green-500 mt-2 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Signatur registrerad
                </p>
              )}
            </div>

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer select-none bg-neutral-900 border border-neutral-800 rounded-lg p-4">
              <input
                type="checkbox"
                name="accepted_terms"
                checked={form.accepted_terms}
                onChange={handleChange}
                data-testid="accept-terms-checkbox"
                className="w-4 h-4 mt-0.5 accent-[#2e3104]"
              />
              <span className="text-sm text-neutral-300">
                {t.terms}{" "}
                <a
                  href="/villkor#hyresavtal"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#a3a63c] underline"
                >
                  {t.termsLinkLabel}
                </a>
              </span>
            </label>

            {/* Submit */}
            <div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="sign-agreement-submit-btn"
                className="w-full h-12 bg-[#2e3104] hover:bg-[#3d4a1f] text-white text-base font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {t.submitting}
                  </>
                ) : (
                  t.submit
                )}
              </Button>
            </div>
          </form>

          <div className="text-center text-xs text-neutral-500 mt-8">
            {t.footer}
          </div>
        </div>
      </div>
    </HelmetProvider>
  );
}
